import { ConnectDropTarget, DropTargetMonitor, useDrop } from 'react-dnd';
import { useDispatch } from 'react-redux';
import { AnyAction, Dispatch } from 'redux';
import { DragSourceType } from 'const';
import { addSSI, moveSSI } from 'containers/Artboards/actions';
import { addCarousel, addLayoutRequest, dropGroupReusableLayout, dropReusableLayout, moveLayout } from 'containers/Layouts/actions';
import { CarouselItem } from 'containers/Layouts/sagas/addCarousel';
import * as Models from 'models';

const InteractiveElementsSourceTypes: DragSourceType[] = [
  DragSourceType.CAROUSEL,
];

export const LayoutDragSourceTypes: DragSourceType[] = [
  DragSourceType.ARTBOARD_GROUP_LAYOUT,
  DragSourceType.ARTBOARD_LAYOUT,
  DragSourceType.ARTBOARD_REUSABLE_LAYOUT,
  DragSourceType.GROUP_LAYOUT,
  DragSourceType.GROUP_LAYOUT_REUSABLE_LAYOUT,
  DragSourceType.LAYOUT,
  DragSourceType.REUSABLE_LAYOUT,
  ...InteractiveElementsSourceTypes,
];

export const SSIDragSourceTypes: DragSourceType[] = [
  DragSourceType.ARTBOARD_SSI,
  DragSourceType.SSI,
];

export const ALLOWED_TO_DROP_ITEMS: DragSourceType[] = [
  ...LayoutDragSourceTypes,
  ...SSIDragSourceTypes,
];

export type DragItemData = {
  screen?: Models.ScreenMap;
  isReadOnly?: boolean;
  document?: Models.DocumentMap;
};

const elementTypeMatchesScreenType = (sectionDisplayName: string, payload: DragItemData): boolean => {
  const { screen, document } = payload;
  let sourceGroupName = screen?.get('name') ?? '';

  if (!sourceGroupName) {
    sourceGroupName = document?.get('classification') ?? '';
  }

  return sourceGroupName.toLowerCase().indexOf(sectionDisplayName.toLowerCase()) === 0;
};

export const dropLayoutFn = (sectionId: string, monitor: DropTargetMonitor, dispatch: Dispatch<AnyAction>, carouselItem?: CarouselItem): void => {
  const itemType = monitor.getItemType() as DragSourceType;

  switch (itemType) {
    case DragSourceType.ARTBOARD_GROUP_LAYOUT:
    case DragSourceType.ARTBOARD_LAYOUT:
    case DragSourceType.ARTBOARD_REUSABLE_LAYOUT:
    case DragSourceType.GROUP_LAYOUT_REUSABLE_LAYOUT: {
      const { layout } = monitor.getItem() as Models.ArtboardLayoutDragObject | Models.ArtboardGroupLayoutDragObject;
      const layoutId = ((layout as unknown) as Models.BaseLayoutMap).get('id');
      dispatch(moveLayout(layoutId, sectionId, carouselItem));
      break;
    }
    case DragSourceType.ARTBOARD_SSI: {
      dispatch(moveSSI(sectionId));
      break;
    }
    case DragSourceType.GROUP_LAYOUT: {
      const { document, storyCardId, isReadOnly } = monitor.getItem() as Models.LayoutDragObject;
      const documentGroupLayout = document as Models.GroupLayoutDocumentMap;
      dispatch(dropGroupReusableLayout(documentGroupLayout.get('id'), sectionId, storyCardId, carouselItem, isReadOnly));
      break;
    }
    case DragSourceType.LAYOUT: {
      const { type, size } = monitor.getItem() as Models.EmptyLayoutDragObject;
      dispatch(addLayoutRequest(type, size, sectionId, carouselItem));
      break;
    }
    case DragSourceType.REUSABLE_LAYOUT: {
      const { document, storyCardId, isReadOnly } = monitor.getItem() as Models.LayoutDragObject;
      const documentReusableLayout = document as Models.ReusableLayoutMap;
      dispatch(dropReusableLayout(documentReusableLayout.get('id'), sectionId, storyCardId, carouselItem, isReadOnly));
      break;
    }
    case DragSourceType.SSI: {
      dispatch(addSSI(sectionId));
      break;
    }
    case DragSourceType.CAROUSEL: {
      dispatch(addCarousel(sectionId));
      break;
    }
  }
};

const canDropFn = (sectionDisplayName: string, hasProjectMetadataToken: boolean, monitor: DropTargetMonitor): boolean => {
  const itemType = monitor.getItemType() as DragSourceType;
  if (!monitor.isOver({ shallow: true })) {
    return false;
  }

  if (!ALLOWED_TO_DROP_ITEMS.includes(itemType)) {
    return false;
  }

  const payload = monitor.getItem() as DragItemData;
  const { isReadOnly } = payload;
  if (!isReadOnly) {
    return true;
  }

  if (hasProjectMetadataToken) {
    return false;
  }

  return elementTypeMatchesScreenType(sectionDisplayName, payload);
};

type SectionDropTargetHook = {
  connectDropTarget: ConnectDropTarget;
  isOver: boolean;
  canDrop: boolean;
};

export const useSectionDropTarget = (sectionId: string,
  sectionDisplayName: string,
  hasProjectMetadataToken = false): SectionDropTargetHook => {
  const dispatch = useDispatch();
  const [{ isOver, canDrop }, connectDropTarget] = useDrop({
    accept: ALLOWED_TO_DROP_ITEMS,
    canDrop: (_, monitor) => canDropFn(sectionDisplayName, hasProjectMetadataToken, monitor),
    drop: (_, monitor) => dropLayoutFn(sectionId, monitor, dispatch),
    collect: monitor => ({
      isOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop(),
    }),
  });

  return {
    connectDropTarget,
    isOver,
    canDrop,
  };
};
